import { lazy } from 'react';

const RELOAD_AT_KEY = 'chunk_reload_at';
const RETRY_DELAY_MS = 1000;
// Don't reload again if we already reloaded within this window (prevents loops)
const RELOAD_COOLDOWN_MS = 30 * 1000;

/**
 * Detects errors thrown when a lazily imported JS chunk cannot be fetched
 * (network dropped while idle, or a new deploy removed the old hashed chunk).
 */
export const isChunkLoadError = error => {
  const message = String(error?.message || error || '');
  return (
    error?.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Unable to preload CSS/i.test(message)
  );
};

/**
 * Reloads the page to pick up fresh chunks. Returns false if we already
 * reloaded within the cooldown window, so a persistent failure never loops.
 */
export const reloadOnceForChunkError = () => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
    if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — can't guard against loops, so don't reload
    return false;
  }
  window.location.reload();
  return true;
};

/**
 * Drop-in replacement for React.lazy that retries a failed chunk import once,
 * then falls back to a single full page reload.
 */
const lazyWithRetry = importer =>
  lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      try {
        return await importer();
      } catch (retryError) {
        if (isChunkLoadError(retryError) && reloadOnceForChunkError()) {
          // Keep Suspense fallback visible until the reload happens
          return new Promise(() => {});
        }
        throw retryError;
      }
    }
  });

export default lazyWithRetry;
